import { IoIosRemoveCircleOutline } from '@react-icons/all-files/io/IoIosRemoveCircleOutline'
import { useEffect, useState } from 'react'
import useAsync from 'react-use/esm/useAsync'
import { CurieSelect } from '~/components/common/CurieSelect'
import { MonacoTextEditor } from '~/components/editors/monaco/MonacoTextEditor'
import { SimpleTextarea } from '~/components/common/SimpleTextarea'
import {
  AgentRequest,
  EdiFactSchemaType,
  EdiSchemaType,
  SourceOutputExamples,
  SourceOutputExampleType,
  sourceOutputExampleTypeFromJSON,
  sourceOutputExampleTypeToJSON
} from '~/protos/agents'
import { toCamelCase } from '~/utils/shared'
import { cm } from '~/utils/utils'
import { TaskParams } from '~/components/task-forms/BaseTaskForm'
import { InfoTooltip } from '~/components/task-forms/shared/InfoToolTip'
import { Edi, EdiSchemaData } from '~/components/task-forms/shared/input-output-form/Edi'
import {
  EdiFact,
  EdifactSchemaData
} from '~/components/task-forms/shared/input-output-form/EdiFact'
import { Hl7, Hl7SchemaData } from '~/components/task-forms/shared/input-output-form/Hl7'
import styles from './CampaignInputOutputToDWLForm.module.scss'

interface CampaignInputOutputToDWLFormProps {
  onChange: (task: TaskParams) => void
  maxRows?: number
  minRows?: number
  collapsible?: boolean
  inputDefaultValue?: string
  inputDefaultEdiSchemaType?: EdiSchemaType
  inputDefaultEdiFactSchemaType?: EdiFactSchemaType
  outputDefaultValue?: string
  inputClassName?: string
  inputTypeDefaultValue?: string
  handleEditorSourceChange?: (value: string) => void
  handleEditorOutputChange?: (value: string) => void
  onError?: () => void
  onErrorClear?: () => void
  vmAware?: boolean
  isReadOnly?: boolean
  // Schema support
  sourceDataType?: string
  onSourceDataTypeChange?: (dataType: string) => void
  ediSchemaData?: EdiSchemaData | null
  onEdiSchemaChange?: (data: EdiSchemaData) => void
  edifactSchemaData?: EdifactSchemaData | null
  onEdifactSchemaChange?: (data: EdifactSchemaData) => void
  hl7SchemaData?: Hl7SchemaData | null
  onHl7SchemaChange?: (data: Hl7SchemaData) => void
}

export const InputOutputExamples = {
  [sourceOutputExampleTypeToJSON(SourceOutputExampleType.DATA_TYPE_XML)]: `
<root>
  <person>
    <name>John Doe</name>
    <age>30</age>
    <city>New York</city>
  </person>
</root>
    `.trim(),
  [sourceOutputExampleTypeToJSON(SourceOutputExampleType.DATA_TYPE_JSON)]: `
{
  "person": {
    "name": "John Doe",
    "age": 30,
    "city": "New York"
  }
}
    `.trim(),
  [sourceOutputExampleTypeToJSON(SourceOutputExampleType.DATA_TYPE_CSV)]: `
name,age,city
John Doe,30,New York
    `.trim(),
  [sourceOutputExampleTypeToJSON(SourceOutputExampleType.DATA_TYPE_YAML)]: `
person:
  name: John Doe
  age: 30
  city: New York
    `.trim()
}

const mapSourceDataTypeToLanguage = (sourceDataType: string) => {
  switch (sourceDataType) {
    case 'DATA_TYPE_JSON':
      return 'json'
    case 'DATA_TYPE_CSV':
      return 'csv'
    case 'DATA_TYPE_YAML':
      return 'yaml'
    case 'DATA_TYPE_XML':
      return 'xml'
    case 'DATA_TYPE_EDI':
      return 'edi'
    case 'DATA_TYPE_EDIFACT':
      return 'edifact'
    default:
      return 'xml'
  }
}

export function CampaignInputOutputToDWLForm({
  onChange,
  minRows = 10,
  maxRows = 13,
  collapsible = false,
  inputTypeDefaultValue,
  inputDefaultValue,
  inputDefaultEdiSchemaType,
  inputDefaultEdiFactSchemaType,
  outputDefaultValue,
  inputClassName,
  handleEditorSourceChange,
  handleEditorOutputChange,
  onError,
  onErrorClear,
  vmAware = false,
  isReadOnly = false,
  // Schema props
  sourceDataType: externalSourceDataType,
  onSourceDataTypeChange,
  ediSchemaData,
  onEdiSchemaChange,
  edifactSchemaData,
  onEdifactSchemaChange,
  hl7SchemaData,
  onHl7SchemaChange
}: CampaignInputOutputToDWLFormProps) {
  const [samples, setSamples] = useState<SourceOutputExamples[]>([
    SourceOutputExamples.fromJSON({
      sourceData: inputDefaultValue,
      outputData: outputDefaultValue,
      sourceDataType: sourceOutputExampleTypeFromJSON(inputTypeDefaultValue),
      inputEdiSchemaType: inputDefaultEdiSchemaType,
      inputEdiFactSchemaType: inputDefaultEdiFactSchemaType
    })
  ])
  const [inputOutputNotes, setInputOutputNotes] = useState<string>('')
  const [internalSourceDataType, setInternalSourceDataType] = useState<string>(
    inputTypeDefaultValue || sourceOutputExampleTypeToJSON(SourceOutputExampleType.DATA_TYPE_XML)
  )
  const [sourceDataValidationError, setSourceDataValidationError] = useState<boolean>(false)
  const [outputDataValidationError, setOutputDataValidationError] = useState<boolean>(false)
  const [vmArgs, setVMArgs] = useState<string>('')

  // Use external source data type if provided, otherwise use internal state
  const sourceDataType = externalSourceDataType || internalSourceDataType

  useEffect(() => {
    if (sourceDataValidationError || outputDataValidationError) {
      onError?.()
    } else {
      onErrorClear?.()
    }
  }, [sourceDataValidationError, outputDataValidationError])

  useAsync(async () => {
    const text =
      '### Sample Input Data\n' +
      samples[0].sourceData +
      '\n### Sample Output Data\n' +
      samples[0].outputData +
      '\n### Notes\n' +
      inputOutputNotes

    const request = AgentRequest.fromPartial({
      command: { commandString: text },
      sourceOutputExamples: samples.map(example =>
        SourceOutputExamples.fromJSON({
          sourceData: example.sourceData,
          outputData: example.outputData,
          sourceDataType: sourceOutputExampleTypeFromJSON(sourceDataType),
          inputEdiSchemaType: example.inputEdiSchemaType,
          inputEdiFactSchemaType: example.inputEdiFactSchemaType
        })
      ),
      inputOutputNotes,
      vmArgs
    })
    onChange?.({ request })
  }, [samples, sourceDataType, inputOutputNotes, vmArgs])

  const items = Object.keys(SourceOutputExampleType)
    .filter(
      type =>
        type.startsWith('DATA_') &&
        !type.endsWith('UNKNOWN') &&
        !type.endsWith('TEXT') &&
        !type.endsWith('MULTIPART')
    )
    .map(key => {
      const label = toCamelCase(key).split(' ').pop()!.toUpperCase()
      return { label, key: key }
    })

  const [sampleInputErrors, setSampleInputErrors] = useState<string[]>(
    Array.from({ length: samples.length }, () => '')
  )
  const [sampleOutputErrors, setSampleOutputErrors] = useState<string[]>(
    Array.from({ length: samples.length }, () => '')
  )

  const handleExampleDelete = (index: number) => {
    setSamples(samples.filter((_, i) => i !== index))
  }

  const handleSourceDataTypeChange = (dataType: string) => {
    if (onSourceDataTypeChange) {
      onSourceDataTypeChange(dataType)
    } else {
      setInternalSourceDataType(dataType)
    }
  }

  return (
    <div className='input-output-form'>
      <div className='input-type'>
        <div className='label font-medium text-md text-[#717680] mb-[10px] mt-[5px]'>
          Sample Input Format
        </div>
        <CurieSelect
          items={items}
          placeholder='Select input format'
          selectedKeys={[sourceDataType]}
          disabled={isReadOnly}
          onSelectionChange={selectedKeys => {
            if (typeof selectedKeys === 'string' || selectedKeys.size === 0) {
              return
            }
            const format = selectedKeys.values().next().value as string
            handleSourceDataTypeChange(format)
          }}
        />
      </div>

      {/* Schema Type Components */}
      {ediSchemaData !== undefined && onEdiSchemaChange && (
        <Edi
          sourceDataType={sourceDataType}
          samples={samples}
          onChange={setSamples}
          onSchemaChange={onEdiSchemaChange}
        />
      )}
      {edifactSchemaData !== undefined && onEdifactSchemaChange && (
        <EdiFact
          sourceDataType={sourceDataType}
          samples={samples}
          onChange={setSamples}
          onSchemaChange={onEdifactSchemaChange}
        />
      )}
      {hl7SchemaData !== undefined && onHl7SchemaChange && (
        <Hl7
          sourceDataType={sourceDataType}
          samples={samples}
          onChange={setSamples}
          onSchemaChange={onHl7SchemaChange}
        />
      )}

      <div>
        {samples.map((example, index) => (
          <div className='flex w-full h-full relative' key={index}>
            <div className='tool-tip-target input-example w-[1px] h-[1px] relative top-[200px] left-[200px]'></div>
            <div className='tool-tip-target output-example w-[1px] h-[1px] relative top-[200px] left-[600px]'></div>
            <div className='input-example w-1/2 flex-grow mb-4'>
              {index === 0 ? (
                <div className='label font-medium text-md text-[#717680] mb-[10px] mt-[5px]'>
                  Sample Input Data:
                </div>
              ) : null}
              <MonacoTextEditor
                value={example.sourceData}
                language={mapSourceDataTypeToLanguage(sourceDataType)}
                onChange={value => {
                  const newExamples = [...samples]
                  newExamples[index].sourceData = value
                  handleEditorSourceChange?.(value)
                  setSamples(newExamples)
                }}
                onError={() => setSourceDataValidationError(true)}
                onErrorClear={() => setSourceDataValidationError(false)}
                disabled={isReadOnly}
              />
              {sampleInputErrors[index] && (
                <div className='text-red-500 text-xs mt-1'>{sampleInputErrors[index]}</div>
              )}
              {index > 0 && (
                <button
                  onClick={() => handleExampleDelete(index)}
                  className='absolute -right-8 top-0 p-1 text-gray-500 hover:text-red-500 transition-colors'
                  aria-label='Delete example'
                >
                  <IoIosRemoveCircleOutline size={24} />
                </button>
              )}
            </div>
            <div className='output-example w-1/2 flex-grow mb-4'>
              {index === 0 ? (
                <div className='label font-medium text-md text-[#717680] mb-[10px] mt-[5px]'>
                  Sample Output Data:
                </div>
              ) : null}
              <MonacoTextEditor
                value={example.outputData}
                className='border-l-0'
                onChange={value => {
                  const newExamples = [...samples]
                  newExamples[index].outputData = value
                  handleEditorOutputChange?.(value)
                  setSamples(newExamples)
                }}
                onError={() => setOutputDataValidationError(true)}
                onErrorClear={() => setOutputDataValidationError(false)}
                disabled={isReadOnly}
              />
            </div>
            {sampleOutputErrors[index] && (
              <div className='text-red-500 text-xs mt-1'>{sampleOutputErrors[index]}</div>
            )}
          </div>
        ))}
      </div>
      {collapsible ? (
        <details className={cm('hidden details', styles.details)}>
          <summary className='summary'>Advanced</summary>
          <div className='description pl-4'>
            <div className='label font-semibold text-xs text-default-700 mb-[10px] mt-[10px]'>
              Notes:
            </div>
            <SimpleTextarea
              onChange={setInputOutputNotes}
              minRows={2}
              placeholder='Additional notes and instructions to help understand your needs better. Could be about some specific transformations you want, some example code snippets, etc.'
            />
          </div>
        </details>
      ) : (
        <div className='description'>
          <div className='label font-semibold text-xs text-default-700 mb-[10px] mt-[10px]'>
            Notes (Optional):
          </div>
          <SimpleTextarea
            onChange={setInputOutputNotes}
            minRows={2}
            placeholder='Additional notes and instructions to help understand your needs better. Could be about some specific transformations you want, some example code snippets, etc.'
          />
          {vmAware ? (
            <>
              <div className='label font-semibold text-xs text-default-700 mb-[10px] mt-[10px] flex justify-between items-center'>
                <span>VM Args:</span>
                <InfoTooltip content='If running the test requires VM arguments, users can supply them to ensure the tests execute correctly within the appropriate environment settings.' />
              </div>
              <SimpleTextarea
                className='mx-0'
                onChange={setVMArgs}
                minRows={2}
                placeholder='e.g. -Denv=test -Dmunit.test.timeout=10000 -Dkey=value'
              />
            </>
          ) : null}
        </div>
      )}
    </div>
  )
}
